import {IssueOpenedIcon, ArrowRightIcon, LockIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {graphql} from 'relay-runtime'
import {BUTTON_LABELS, LABELS, type OptionConfig} from '../..'
import {useFragment} from 'react-relay'
import type {OptionsSectionFragment$key} from './__generated__/OptionsSectionFragment.graphql'
import {useMemo, useRef, useState} from 'react'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {IssueConversationLock} from '../header/IssueConversationLock'
import {IssueDeletionConfirmationDialog} from '../header/IssueDeletionConfirmationDialog'
import {IssueTransferDialog} from '../header/IssueTransferDialog'
import {IssueTypesDialog} from '../header/IssueTypesDialog'

const OptionsSectionFragment = graphql`
  fragment OptionsSectionFragment on Issue {
    id
    isTransferInProgress
    locked
    viewerCanUpdateNext
    viewerCanDelete
    viewerCanTransfer
    repository {
      id
      name
      owner {
        login
      }
      isPrivate
      isExcludedFromIssueTypes
      issueTypes(first: 10) {
        ...IssueTypesDialogIssueTypes
      }
    }
    issueType {
      ...IssueTypesDialogIssueType
    }
  }
`

export type OptionsSectionProps = {
  issue: OptionsSectionFragment$key
  optionConfig: OptionConfig
}

export function OptionsSection({issue, optionConfig}: OptionsSectionProps) {
  const [showIssueTransferDialog, setShowIssueTransferDialog] = useState(false)
  const [showIssueDeletionConfirmationDialog, setShowIssueDeletionConfirmationDialog] = useState(false)
  const [showConversationLockDialog, setShowConversationLockDialog] = useState(false)
  const [showIssueTypesDialog, setShowIssueTypesDialog] = useState(false)

  const {
    id,
    viewerCanUpdateNext,
    viewerCanTransfer,
    viewerCanDelete,
    isTransferInProgress,
    issueType,
    locked,
    repository,
  } = useFragment(OptionsSectionFragment, issue)

  const analyticsMetadata = useMemo(
    () => ({
      owner: repository?.owner?.login ?? '',
      repositoryName: repository?.name ?? '',
      issueId: id,
    }),
    [id, repository?.name, repository?.owner?.login],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <ActionList>
        {viewerCanUpdateNext && (
          <ActionList.Item onSelect={() => setShowConversationLockDialog(true)}>
            <ActionList.LeadingVisual>
              <LockIcon />
            </ActionList.LeadingVisual>
            {locked ? LABELS.lock.buttonConfirmUnlock : LABELS.lock.buttonConfirmLock}
          </ActionList.Item>
        )}
        {/* pin option should be added here */}
        {viewerCanUpdateNext && repository.issueTypes && !repository.isExcludedFromIssueTypes && (
          <ActionList.Item onSelect={() => setShowIssueTypesDialog(true)}>
            <ActionList.LeadingVisual>
              <IssueOpenedIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.changeIssueType}
          </ActionList.Item>
        )}
        {viewerCanTransfer && (
          <ActionList.Item onSelect={() => setShowIssueTransferDialog(true)}>
            <ActionList.LeadingVisual>
              <ArrowRightIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.transferIssue}
          </ActionList.Item>
        )}

        {viewerCanDelete && (
          <ActionList.Item variant="danger" onSelect={() => setShowIssueDeletionConfirmationDialog(true)}>
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            {BUTTON_LABELS.deleteIssue}
          </ActionList.Item>
        )}
      </ActionList>
      {showIssueTransferDialog && (
        <IssueTransferDialog
          owner={repository.owner.login}
          repository={repository.name}
          isRepoPrivate={repository.isPrivate}
          issueId={id}
          isTransferInProgress={isTransferInProgress}
          onClose={() => setShowIssueTransferDialog(false)}
        />
      )}
      {viewerCanUpdateNext && repository.issueTypes && (
        <AnalyticsProvider appName="issue_types" category="issue_viewer" metadata={analyticsMetadata}>
          <IssueTypesDialog
            onClose={() => setShowIssueTypesDialog(false)}
            issueId={id}
            issueTypes={repository.issueTypes}
            initialSelectedType={issueType}
            isOpen={showIssueTypesDialog}
            returnFocusRef={anchorRef}
          />
        </AnalyticsProvider>
      )}
      {showConversationLockDialog && (
        <IssueConversationLock issueId={id} isUnlocked={!locked} onClose={() => setShowConversationLockDialog(false)} />
      )}
      {showIssueDeletionConfirmationDialog && (
        <IssueDeletionConfirmationDialog
          issueId={id}
          onSuccessfulDeletion={optionConfig.onIssueDelete}
          afterDelete={optionConfig.navigateBack}
          onClose={() => setShowIssueDeletionConfirmationDialog(false)}
        />
      )}
    </>
  )
}

try{ OptionsSection.displayName ||= 'OptionsSection' } catch {}